import * as PIXI from "pixi.js";
import Layers from "../../../../../../src/layers/Layers";
import { Coord } from "../../../../../../src/types/Interface";
import calcBgCorners from "./helpers/calcBgCorners";
import rotateCoordinate from "../../../../../../src/utils/coord/rotateCoordinate";
import calcRotateSymbolCenter from "./helpers/calcRotateSymbolCenter";
import getAngleBetweenPoints from "../../../../../../src/utils/math/getAngleBtwPoints";
import getRotatedOffset from "../../../../../../src/utils/coord/getRotatedOffset";


const TRACING_COLOR = 0x0000FF;


export default class TracingPaperRenderer extends PIXI.Container {
  private Layers = Layers.getInstance();  
  private bg = this.addChild(new PIXI.Graphics());
  private shape = this.addChild(new PIXI.Graphics());
  private pivotPls = this.addChild(new PIXI.Graphics());
  private rotateSymbol = this.addChild(new PIXI.Graphics());
  constructor() {
    super();
    this.addToLayer();
  }
  private addToLayer = () => this.Layers.UI.addChild(this);
  clearAll = () => [this.bg, this.shape, this.pivotPls, this.rotateSymbol].forEach(g => g.clear());
  setInteractivity (v: boolean) {
      this.eventMode = v ? "static" : "none";
  }
  drawPivotPls(pivotCoord: Coord, rotRadian: number){
    const PlusLength = 16;
    const lX1 = Math.cos(rotRadian) * PlusLength;
    const lY1 = Math.sin(rotRadian) * PlusLength;
    const lX2 = Math.cos(rotRadian + Math.PI / 2) * PlusLength;
    const lY2 = Math.sin(rotRadian + Math.PI / 2) * PlusLength;

    const pC = { ...pivotCoord };
    this.pivotPls
      .clear()
      .moveTo(pC.x + lX1, pC.y + lY1)
      .lineTo(pC.x - lX1, pC.y - lY1)

      .moveTo(pC.x + lX2, pC.y + lY2)
      .lineTo(pC.x - lX2, pC.y - lY2)
      .stroke({ width: 7, color: TRACING_COLOR, cap: "round" })
  }

	drawShape( shapeCoords: Coord[], pivotCoord: Coord, rotRadian: number) {
    const rotatedCoords = shapeCoords.map(coord => rotateCoordinate(coord, pivotCoord, rotRadian));
    
    this.shape.clear()
    rotatedCoords.forEach((coord, cI) => {
      if (cI == 0) {
        this.shape.moveTo(coord.x, coord.y);
      } else {
        this.shape.lineTo(coord.x, coord.y);
        if (cI == rotatedCoords.length - 1) {
          this.shape
            .lineTo(rotatedCoords[0].x, rotatedCoords[0].y)
            .stroke({ color: TRACING_COLOR, width: 7, cap: "round" });
        }
      }
    });
  }

	drawBg(gridCellSize: number, shapeCoords: Coord[], pivotCoord: Coord, rotRadian: number): Coord[] {
    const corners = calcBgCorners(shapeCoords, pivotCoord, gridCellSize)
    const rotatedCorners = corners.map(coord => rotateCoordinate(coord, pivotCoord, rotRadian));

    this.bg
      .clear()
      .moveTo(rotatedCorners[0].x, rotatedCorners[0].y)
      .lineTo(rotatedCorners[1].x, rotatedCorners[1].y)
      .lineTo(rotatedCorners[2].x, rotatedCorners[2].y)
      .lineTo(rotatedCorners[3].x, rotatedCorners[3].y)
      .lineTo(rotatedCorners[0].x, rotatedCorners[0].y)
      .fill({ color: 0x000000, alpha: .25 });

      return rotatedCorners;
  }
  drawRotateSymbol(bgCoords: Coord[], totalRotation: number) {
    const radius = 64;
    const center = calcRotateSymbolCenter(bgCoords, totalRotation);
    const rotRadian = getAngleBetweenPoints(bgCoords[0], bgCoords[1]);
    const startRadian = -Math.PI / 6 * 5 + rotRadian;
    const endRadian = -Math.PI / 6 * 4 + rotRadian;

    // 1. Calc Coords
    const startCoord = {
      x: center.x + Math.cos(startRadian) * radius,
      y: center.y + Math.sin(startRadian) * radius
    };
    const endCoord = {
      x: center.x + Math.cos(endRadian) * radius,
      y: center.y + Math.sin(endRadian) * radius
    };

    this.rotateSymbol
      .clear()
      // Arc
      .moveTo(startCoord.x, startCoord.y)
      .arc(center.x, center.y, radius, startRadian, endRadian)
      .stroke({ color: 0x000000, width: 7, cap: "round" });

    // --- START ARROW ---
    const sP1 = getRotatedOffset(5, 3, rotRadian);
    const sP2 = getRotatedOffset(-5, 9, rotRadian);
    const sP3 = getRotatedOffset(-4, -4, rotRadian);

    this.rotateSymbol
      .moveTo(startCoord.x, startCoord.y)
      .lineTo(startCoord.x + sP1.x, startCoord.y + sP1.y)
      .lineTo(startCoord.x + sP2.x, startCoord.y + sP2.y)
      .lineTo(startCoord.x + sP3.x, startCoord.y + sP3.y);

    // --- END ARROW ---
    const eP1 = getRotatedOffset(3, 5, rotRadian);
    const eP2 = getRotatedOffset(9, -5, rotRadian);
    const eP3 = getRotatedOffset(-4, -4, rotRadian);

    this.rotateSymbol
      .moveTo(endCoord.x, endCoord.y)
      .lineTo(endCoord.x + eP1.x, endCoord.y + eP1.y)
      .lineTo(endCoord.x + eP2.x, endCoord.y + eP2.y)
      .lineTo(endCoord.x + eP3.x, endCoord.y + eP3.y)
        
      .stroke({ color: 0x000000, width: 5, cap: "round" })
      .fill(0x000000);
}
}